/**
 * Client-side Meta tracking utilities for fbclid capture and cookie reading.
 * Used to enrich Segment track calls with Meta attribution data (fbc, fbp).
 * Based on facebook docs.
 */
import type { MiddlewareFunction } from '@segment/analytics-next';
import { getCkyConsentCategories } from '@/util/cookie_consent_client';

/**
 * Capture fbclid from URL search params and store as _fbc cookie.
 * Meta uses this to attribute conversions back to ad clicks.
 * Format: fb.1.{timestamp}.{fbclid}
 * No-op if fbclid param is absent.
 */
export function captureFbclid(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');

  if (fbclid) {
    const timestamp = Date.now();
    const fbc = `fb.1.${timestamp}.${fbclid}`;
    const expires = new Date(
      Date.now() + 90 * 24 * 60 * 60 * 1000
    ).toUTCString();
    document.cookie = `_fbc=${fbc};expires=${expires};path=/;`;
  }
}

/**
 * Read Meta tracking cookies (_fbc and _fbp) from document.cookie.
 * _fbc: Facebook click ID (set by captureFbclid or Meta Pixel)
 * _fbp: Facebook browser ID (set by Meta Pixel)
 */
export function getMetaTrackingCookies(): {
  fbc?: string;
  fbp?: string;
} {
  if (typeof document === 'undefined') {
    return {};
  }

  const cookies: Record<string, string> = document.cookie.split(';').reduce(
    (result, cookie) => ({
      ...result,
      [(cookie.split('=')[0] || '').trim()]: (cookie.split('=')[1] || '').trim()
    }),
    {}
  );

  return {
    fbc: cookies._fbc || undefined,
    fbp: cookies._fbp || undefined
  };
}

/**
 * Segment source middleware that puts fbc/fbp on page events so the Facebook
 * Conversions API can match server-side PageViews. Conversion events already
 * send these from the call site, which is why they match far better today.
 *
 * Page events only, so ad IDs don't end up on every track event body. Gated on
 * advertisement consent because fbc/fbp are advertising identifiers and
 * analytics consent on its own isn't enough to send Meta a click ID.
 */
export const metaTrackingMiddleware: MiddlewareFunction = ({
  payload,
  next
}) => {
  if (payload.obj.type === 'page' && getCkyConsentCategories().advertisement) {
    const { fbc, fbp } = getMetaTrackingCookies();
    const properties = payload.obj.properties ?? {};

    if (fbc) properties.fbc = fbc;
    if (fbp) properties.fbp = fbp;

    payload.obj.properties = properties;
  }

  next(payload);
};
